import { useRef, useState, useEffect } from "react";
import Hls from "hls.js";
import { Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";

// Resolve /api/* to the backend URL. Absolute URLs pass through unchanged.
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveSrc = (src) => {
  if (!src) return src;
  if (src.startsWith("/api/")) return `${BACKEND}${src}`;
  return src;
};

export default function VideoPlayer({ src: rawSrc, streamType = "mp4", poster, title, subtitle, autoPlay = true, liveBadge = true, onProgress, initialPosition = 0 }) {
  const src = resolveSrc(rawSrc);
  const ref = useRef(null);
  const [playing, setPlaying] = useState(autoPlay);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [needsClickToPlay, setNeedsClickToPlay] = useState(false);
  const lastReport = useRef(0);

  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return;
    let hls;
    v.muted = true;

    const attach = () => {
      if (streamType === "hls" && src && src.includes(".m3u8")) {
        if (Hls.isSupported()) {
          hls = new Hls({ enableWorker: true });
          hls.loadSource(src);
          hls.attachMedia(v);
          hls.on(Hls.Events.ERROR, (_, data) => { if (data.fatal) console.warn("HLS error", data); });
        } else if (v.canPlayType("application/vnd.apple.mpegurl")) {
          v.src = src;
        }
      } else {
        v.src = src;
      }
      if (initialPosition > 0) v.currentTime = initialPosition;
      v.load();
    };
    attach();

    const tryPlay = () => {
      if (!autoPlay) return;
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.then(() => { setPlaying(true); setNeedsClickToPlay(false); })
         .catch((err) => { console.warn("autoplay blocked:", err?.name); setPlaying(false); setNeedsClickToPlay(true); });
      }
    };
    const onCanPlay = () => tryPlay();
    const onLoadedData = () => tryPlay();
    const onError = () => { console.warn("video error"); setPlaying(false); setNeedsClickToPlay(true); };
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("loadeddata", onLoadedData);
    v.addEventListener("error", onError);
    v.addEventListener("stalled", onError);

    const onTime = () => {
      if (!v.duration) return;
      setProgress((v.currentTime / v.duration) * 100);
      const now = Date.now();
      if (onProgress && now - lastReport.current > 10000) {
        lastReport.current = now;
        onProgress(v.currentTime, v.duration);
      }
    };
    v.addEventListener("timeupdate", onTime);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("error", onError);
      v.removeEventListener("stalled", onError);
      if (hls) hls.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, streamType]);

  const toggle = () => {
    const v = ref.current; if (!v) return;
    if (v.paused) { v.play().then(() => { setPlaying(true); setNeedsClickToPlay(false); }).catch(()=>{}); }
    else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => { const v = ref.current; if (!v) return; v.muted = !v.muted; setMuted(v.muted); };
  const fullscreen = () => { const v = ref.current; if (!v) return; if (v.requestFullscreen) v.requestFullscreen(); };

  return (
    <div className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden group border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.6)]" data-testid="live-video-player">
      <video ref={ref} poster={poster} className="w-full h-full object-cover" loop playsInline muted autoPlay preload="auto" data-testid="video-element">
        {src && <source src={src} type={streamType === "hls" ? "application/vnd.apple.mpegurl" : "video/mp4"} />}
      </video>
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#050507] via-transparent to-transparent" />
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-[#050507]/80 via-transparent to-transparent" />
      {needsClickToPlay && (
        <button onClick={toggle} className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm group-hover:bg-black/50 transition" data-testid="tap-to-play-overlay" aria-label="Play video">
          <span className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center shadow-2xl hover:scale-110 transition">
            <Play className="w-9 h-9 text-[#050507] ml-1" />
          </span>
        </button>
      )}
      <div className="absolute top-5 left-5 flex items-center gap-3">
        {liveBadge && (
          <div className="flex items-center gap-2 bg-[#E11D48]/95 backdrop-blur-md px-3 py-1 rounded-md border border-white/20 shadow-lg" data-testid="live-badge">
            <span className="w-2 h-2 rounded-full bg-white live-dot" />
            <span className="text-xs font-bold tracking-widest text-white">LIVE</span>
          </div>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="mb-4">
          {subtitle && <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.2em] uppercase mb-2">{subtitle}</p>}
          {title && <h2 className="font-heading text-2xl md:text-4xl font-bold text-white leading-tight max-w-2xl">{title}</h2>}
        </div>
        {!liveBadge && <div className="w-full h-1 bg-white/15 rounded-full mb-3 overflow-hidden"><div className="h-full bg-[#D4AF37]" style={{ width: `${progress}%` }} /></div>}
        <div className="flex items-center gap-3">
          <button onClick={toggle} className="w-11 h-11 rounded-full btn-gold flex items-center justify-center" data-testid="player-play-toggle">
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
          <button onClick={toggleMute} className="w-10 h-10 rounded-full btn-ghost flex items-center justify-center" data-testid="player-mute-toggle">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <div className="flex-1" />
          <button onClick={fullscreen} className="w-10 h-10 rounded-full btn-ghost flex items-center justify-center" data-testid="player-fullscreen">
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
